import { isIos, isStandalone, serviceWorkerRegistration } from '../pwa/serviceWorker.js';

/**
 * The one place that touches the browser's Notification API.
 *
 * Everything else in the app asks this module what is possible and hands it a
 * title and a body; nothing else constructs a `Notification` or reads
 * `Notification.permission`. Keeping it behind one file is what makes the
 * capability rules below auditable in a single read - and what would let a
 * service-worker implementation (needed for notifications while the tab is
 * closed) drop in without touching a component.
 *
 * PHONES: Android Chrome and installed iOS apps show notifications only through
 * a service worker (`pwa/serviceWorker.ts`), which is why `show` goes through
 * its registration. While the app is closed, Web Push (`push.ts`) takes over.
 *
 * SECURE CONTEXT: the Notification API is
 * gated on a secure context. On the plain-HTTP VM deployment described in
 * CLAUDE.md, Chrome and Firefox refuse the permission prompt outright, so
 * `availability()` reports `insecure-context` and the UI says so rather than
 * showing a toggle that silently does nothing. Everything here degrades to the
 * in-tab title badge, which works on any origin.
 */

/** Why system notifications are or are not possible on this origin. */
export type NotificationAvailability =
  /** The API exists and the origin is allowed to use it. */
  | 'available'
  /** No Notification API at all - an old browser, or a non-browser runtime. */
  | 'unsupported'
  /** The API exists but the page is not a secure context, so it is inert. */
  | 'insecure-context'
  /**
   * iPhone / iPad in a Safari tab. iOS (16.4+) gives web notifications only to
   * an app added to the Home Screen, so the answer is "install it first".
   */
  | 'needs-install';

export interface SystemNotification {
  title: string;
  body: string;
  /**
   * Collapses notifications: a second one with the same tag REPLACES the first
   * rather than stacking. Channel id, so a burst in one conversation is one
   * entry in the system tray and not fifteen.
   */
  tag: string;
  /** Where a click takes the user, e.g. `/channels/<id>`. */
  url: string;
}

/** How long a notification stays up before it is closed for the user. */
const AUTO_CLOSE_MS = 10_000;

export function availability(): NotificationAvailability {
  if (typeof window === 'undefined') {
    return 'unsupported';
  }

  if (!('Notification' in window)) {
    return isIos() && !isStandalone() ? 'needs-install' : 'unsupported';
  }

  // `isSecureContext` is the same gate the browser applies internally; checking
  // it ourselves is what lets the UI explain the failure instead of the user
  // watching a permission prompt never appear.
  if (!window.isSecureContext) {
    return 'insecure-context';
  }

  return 'available';
}

/** The current grant, or `denied` where the API cannot be used at all. */
export function currentPermission(): NotificationPermission {
  return availability() === 'available' ? Notification.permission : 'denied';
}

/**
 * Asks for permission. MUST be called from a user gesture - browsers reject or
 * silently ignore a prompt raised from a socket event or a timer.
 */
export async function requestPermission(): Promise<NotificationPermission> {
  if (availability() !== 'available') {
    return 'denied';
  }

  try {
    return await Notification.requestPermission();
  } catch {
    // Some browsers throw on a repeated request; the existing grant still stands.
    return Notification.permission;
  }
}

/**
 * Shows a notification, or does nothing if that is not possible.
 *
 * Through the service worker's registration whenever there is one: Android
 * Chrome throws on `new Notification()` ("use showNotification"), and an
 * installed iOS app has only this path. A click is handled in `public/sw.js`,
 * which focuses the app and sends it to `url`. The constructor remains as the
 * fallback for a desktop browser without a worker.
 *
 * Never throws: a notification is a nicety, and a failure here must not break
 * the socket handler it was called from.
 */
export async function show({ title, body, tag, url }: SystemNotification): Promise<void> {
  if (currentPermission() !== 'granted') {
    return;
  }

  const registration = await serviceWorkerRegistration();

  try {
    if (registration) {
      await registration.showNotification(title, {
        body,
        tag,
        icon: '/icons/icon-192.png',
        badge: '/icons/badge-96.png',
        data: { url },
      });
      return;
    }

    const notification = new Notification(title, { body, tag, icon: '/icons/icon-192.png' });

    notification.onclick = () => {
      window.focus();
      notification.close();
      fallbackNavigate?.(url);
    };

    window.setTimeout(() => notification.close(), AUTO_CLOSE_MS);
  } catch {
    // Nothing to do but leave the in-app unread badge to carry the signal.
  }
}

let fallbackNavigate: ((url: string) => void) | null = null;

/** Where a click on a constructor-made (no service worker) notification goes. */
export function setNotificationNavigator(navigate: ((url: string) => void) | null): void {
  fallbackNavigate = navigate;
}
